import { IonContent, IonPage } from "@ionic/react";
import { useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { IncomingCallView } from "@/components/communications/incoming-call-view";
import { IncomingStaffCallView } from "@/components/communications/incoming-staff-call-view";
import { leaveAppEntry } from "@/lib/history-entry";
import { routeTemplates } from "@/lib/routes";
import { useVoice } from "@/lib/twilio/voice-context";

export function IncomingCallPage() {
  const history = useHistory();
  const { pendingInvite, acceptInvite, rejectInvite, activeCall } = useVoice();

  // If the invite vanishes without becoming an active call (remote cancel,
  // rejection, or direct navigation here without an invite), leave the page.
  // The voice context's own `callInviteCancelled` / `callDisconnected`
  // handlers already call goBack when this page is mounted; the pathname
  // guard here prevents a second pop in the post-navigation render.
  useEffect(() => {
    if (pendingInvite || activeCall) {
      return;
    }
    if (history.location.pathname !== routeTemplates.app.incomingCall) {
      return;
    }

    leaveAppEntry(history, "/chats");
  }, [pendingInvite, activeCall, history]);

  const handleAccept = useCallback(() => {
    void acceptInvite();
  }, [acceptInvite]);
  const handleReject = useCallback(() => {
    void rejectInvite();
  }, [rejectInvite]);

  // No invite (vanished mid-transition, or direct nav here): render an empty
  // page while the effect above redirects. Guarding here lets the child views
  // take a non-optional `caller`.
  if (!pendingInvite) {
    return (
      <IonPage>
        <IonContent fullscreen />
      </IonPage>
    );
  }

  // Branch on caller kind: staff-to-staff calls don't have a guest lookup
  // (no phone number → guest mapping) and shouldn't surface a reservation
  // pill.
  const isStaffInvite = pendingInvite.caller.kind === "staff";

  return (
    <IonPage>
      <IonContent fullscreen>
        {isStaffInvite ? (
          <IncomingStaffCallView
            onAccept={handleAccept}
            onReject={handleReject}
            staffMemberId={pendingInvite.caller.staffMemberId}
            staffName={pendingInvite.caller.staffName}
          />
        ) : (
          <IncomingCallView
            caller={pendingInvite.caller}
            dev={pendingInvite.dev}
            onAccept={handleAccept}
            onReject={handleReject}
          />
        )}
      </IonContent>
    </IonPage>
  );
}
